import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { timeAgo } from "@/lib/utils-ros";
import {
  Shield, UserPlus, Loader2, X, Pencil, Ban, RotateCcw, KeyRound, Trash2,
  ChevronRight, ChevronDown, Users, Building2, Check, Search, Plus, ShieldCheck, Filter
} from "lucide-react";

/**
 * Admin · User Management
 * Exposes every RBAC dimension: 6 canonical roles × 13 modules × 9 access levels,
 * plus confidential-field metadata. Super-admin only.
 */

const ROLE_META = {
  super_admin:      { label: "Super Admin",     color: "#DC2626", desc: "Full write across all modules — capped at one account" },
  admin:            { label: "Admin",           color: "#B91C1C", desc: "Full write across all modules except admin-tier accounts" },
  founder:          { label: "Founder / CRO",   color: "#7C3AED", desc: "Read-only on ops; full on approvals" },
  growth_marketing: { label: "Growth · MMK",    color: "#C47D2B", desc: "Marketing, insights, partner assignments" },
  ergoniq_sales:    { label: "Ergoniq Sales",   color: "#0891B2", desc: "Full sales lifecycle for own rows" },
  partner:          { label: "Partner",         color: "#6B7280", desc: "Assigned-scope access only" },
};
const LEVEL_STYLE = {
  full:          { color: "#16A34A", bg: "rgba(22,163,74,0.10)" },
  read:          { color: "#0891B2", bg: "rgba(8,145,178,0.10)" },
  assigned:      { color: "#C47D2B", bg: "rgba(196,125,43,0.10)" },
  assigned_read: { color: "#7C3AED", bg: "rgba(124,58,237,0.10)" },
  own:           { color: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
  own_read:      { color: "#7C3AED", bg: "rgba(124,58,237,0.10)" },
  own_shared:    { color: "#0891B2", bg: "rgba(8,145,178,0.10)" },
  handover:      { color: "#EC4899", bg: "rgba(236,72,153,0.10)" },
  none:          { color: "#9CA3AF", bg: "rgba(0,0,0,0.04)" },
};

export default function Admin() {
  const { role } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const isSuperAdmin = role === "super_admin";
  const isAdminTier = role === "admin";
  const isAdminOrAbove = isSuperAdmin || isAdminTier;

  async function load() {
    if (!isAdminOrAbove) { setLoading(false); return; }
    setLoading(true);
    try {
      const [u, m, p] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/rbac/matrix"),
        api.get("/partners"),
      ]);
      setUsers(u.data);
      setMatrix(m.data);
      setPartners(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load admin data");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [isAdminOrAbove]);

  const filtered = useMemo(() => {
    return users.filter(u => {
      if (roleFilter !== "all" && u.role_canonical !== roleFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s) || (u.partner?.firm || "").toLowerCase().includes(s);
    });
  }, [users, roleFilter, q]);

  const roleCounts = useMemo(() => {
    const acc = { all: users.length };
    for (const u of users) acc[u.role_canonical] = (acc[u.role_canonical] || 0) + 1;
    return acc;
  }, [users]);

  if (!isAdminOrAbove) {
    return (
      <div className="p-10 text-center">
        <Shield size={28} className="mx-auto text-[var(--text-dim)] mb-3" />
        <div className="font-display text-lg mb-1">Admin only</div>
        <div className="text-[13px] text-[var(--text-muted)]">User Administration is restricted to the Admin and Super Admin roles.</div>
      </div>
    );
  }

  async function deactivate(u) {
    if (!window.confirm(`Deactivate ${u.name}? They will lose access immediately but their data is preserved.`)) return;
    try { await api.post(`/admin/users/${u.id}/deactivate`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function reactivate(u) {
    try { await api.post(`/admin/users/${u.id}/reactivate`); toast.success("Reactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function del(u) {
    if (!window.confirm(`Permanently delete ${u.name}? Historical audit entries reference this user by ID and will remain — but the user cannot log in and disappears from lists. This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="min-h-full bg-[#F5FAFC] pb-12" data-testid="admin-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-7 pb-12 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A8D8ED] mb-1.5 flex items-center gap-2">
              <ShieldCheck size={12} /> Admin Console · Super-admin
            </div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight !text-white">User Administration</h1>
            <p className="text-[13px] text-white/75 mt-1 max-w-3xl">
            Manage every account. Each role maps to a full RBAC surface — {matrix ? `${matrix.canonical_roles.length} roles × ${matrix.modules.length} modules × ${Object.keys(matrix.level_definitions).length} access levels.` : "loading…"}
            </p>
          </div>
          <button onClick={() => setCreateOpen(true)} data-testid="admin-create-user"
                  className="h-10 px-4 rounded-xl bg-[#E58A19] hover:bg-[#C96F08] !text-white text-[13px] font-semibold flex items-center gap-2 shadow-[0_7px_18px_rgba(0,0,0,0.18)]">
            <UserPlus size={14} /> Create User
          </button>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-5 relative z-10 space-y-4">
      <div className="inline-flex gap-1 p-1.5 rounded-2xl bg-white border border-[#C9DCE6] shadow-[0_9px_22px_rgba(7,83,154,0.10)]">
        {[
          { k: "users", label: "Users", count: users.length },
          { k: "rbac", label: "RBAC Matrix", count: matrix?.modules?.length || 0 },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
                  className={`h-9 px-4 rounded-xl text-[12px] flex items-center gap-2 transition-colors ${tab === t.k ? "bg-[#07539A] !text-white font-semibold shadow-[0_5px_14px_rgba(7,83,154,0.18)]" : "text-[#607D8B] hover:bg-[#EDF7FB] hover:text-[#07539A]"}`}>
            {t.label} <span className={`font-mono text-[10px] ${tab === t.k ? "!text-white/75" : "text-[#78909C]"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-[var(--text-muted)]"><Loader2 size={16} className="animate-spin mr-2" /> Loading…</div>
      ) : tab === "users" ? (
        <UsersView
          users={filtered} roleCounts={roleCounts} matrix={matrix}
          q={q} setQ={setQ} roleFilter={roleFilter} setRoleFilter={setRoleFilter}
          onEdit={setEditUser} onDeactivate={deactivate} onReactivate={reactivate}
          onReset={setResetUser} onDelete={del}
        />
      ) : (
        <RBACMatrixView matrix={matrix} setMatrix={setMatrix} canEdit={isSuperAdmin} />
      )}
      </div>

      {createOpen && <UserFormModal partners={partners} matrix={matrix} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); load(); }} />}
      {editUser && <UserFormModal user={editUser} partners={partners} matrix={matrix} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} />}
      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}

/* --------- Users view --------- */
function UsersView({ users, roleCounts, matrix, q, setQ, roleFilter, setRoleFilter, onEdit, onDeactivate, onReactivate, onReset, onDelete }) {
  const { role: actorRole } = useAuth();
  const [expanded, setExpanded] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  // Mirrors the backend containment rule: an Admin (not Super Admin) can't manage an
  // existing admin-or-super_admin account at all — hide the action buttons rather than
  // show controls that would just 403.
  const canManage = (u) => actorRole === "super_admin" || !["admin", "super_admin"].includes(u.role_canonical);
  return (
    <>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 bg-white border border-[#BFD6E1] rounded-full h-11 px-4 flex-1 min-w-64 shadow-[0_6px_18px_rgba(7,83,154,0.06)] focus-within:border-[#00A2D4]">
          <Search size={15} className="text-[#07539A]" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, firm" data-testid="admin-users-search"
                 className="flex-1 h-full outline-none text-[13px] bg-transparent text-[#243B53] placeholder:text-[#9AAEB8]" />
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setFilterOpen(open => !open)}
            data-testid="admin-users-filter-btn"
            className={`h-11 px-4 rounded-xl border text-[12px] font-semibold flex items-center gap-2 shadow-[0_6px_18px_rgba(7,83,154,0.06)] ${roleFilter !== "all" ? "border-[#07539A] bg-[#E7F5FB] text-[#07539A]" : "border-[#C9DCE6] bg-white text-[#07539A] hover:bg-[#F3FAFD]"}`}
          >
            <Filter size={14} /> Filter
            {roleFilter !== "all" && (
              <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">1</span>
            )}
          </button>
          {filterOpen && (
            <div className="absolute right-0 top-12 z-30 w-64 rounded-2xl border border-[#C9DCE6] bg-white p-2 shadow-[0_18px_44px_rgba(7,83,154,0.18)]" data-testid="admin-users-filter-menu">
              <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">Filter by role</div>
              {["all", ...(matrix?.canonical_roles || [])].map(roleKey => {
                const active = roleFilter === roleKey;
                const label = roleKey === "all" ? "All users" : (ROLE_META[roleKey]?.label || roleKey);
                return (
                  <button
                    key={roleKey}
                    type="button"
                    onClick={() => { setRoleFilter(roleKey); setFilterOpen(false); }}
                    data-testid={`admin-role-filter-${roleKey}`}
                    className={`w-full px-3 py-2.5 rounded-xl flex items-center gap-2 text-left text-[12px] ${active ? "bg-[#E7F5FB] text-[#07539A] font-semibold" : "text-[#243B53] hover:bg-[#F3FAFD]"}`}
                  >
                    <span className="flex-1">{label}</span>
                    <span className="font-mono text-[10px] text-[#78909C]">{roleCounts[roleKey] || 0}</span>
                    {active && <Check size={13} className="text-[#07539A]" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="border border-[#C9DCE6] rounded-[22px] bg-white overflow-hidden shadow-[0_10px_28px_rgba(7,83,154,0.08)]">
        {users.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-[var(--text-muted)]">No users match.</div>
        ) : (
          <div className="divide-y divide-[#DCEAF0]">
            {users.map(u => {
              const rm = ROLE_META[u.role_canonical] || { label: u.role_canonical, color: "#6B7280" };
              const isExp = expanded === u.id;
              return (
                <div key={u.id} data-testid={`admin-user-${u.id}`}>
                  <div className={`px-4 md:px-5 py-4 flex items-center gap-3 transition-colors ${isExp ? "bg-[#F3FAFD]" : "hover:bg-[#FAFDFE]"}`}>
                    <button onClick={() => setExpanded(isExp ? null : u.id)}
                            data-testid={`admin-user-expand-${u.id}`}
                            className="w-7 h-7 rounded-lg text-[#78909C] hover:text-[#07539A] hover:bg-[#E7F5FB] flex items-center justify-center">
                      {isExp ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-[#DDF1F8] flex items-center justify-center text-[#07539A] text-[11px] font-mono font-semibold shrink-0">
                      {(u.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-semibold text-[#12324A] truncate">{u.name}</span>
                        <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full"
                              style={{ color: rm.color, background: `${rm.color}18` }}>{rm.label}</span>
                        {u.is_deactivated && (
                          <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#E9EEF1] text-[#607D8B]">Deactivated</span>
                        )}
                        {u.partner?.firm && (
                          <span className="text-[10px] text-[#607D8B] inline-flex items-center gap-1">
                            <Building2 size={9} /> {u.partner.firm}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#78909C] mt-0.5">
                        {u.email}{u.last_login_at ? ` · last seen ${timeAgo(u.last_login_at)}` : " · never signed in"}
                        {u.manager?.name ? ` · Manager: ${u.manager.name}` : ""}
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-1 shrink-0">
                      {canManage(u) ? (
                        <>
                          <IconBtn onClick={() => onEdit(u)} icon={Pencil} label="Edit" testid={`admin-edit-${u.id}`} />
                          <IconBtn onClick={() => onReset(u)} icon={KeyRound} label="Reset password" testid={`admin-reset-${u.id}`} />
                          {u.is_deactivated
                            ? <IconBtn onClick={() => onReactivate(u)} icon={RotateCcw} label="Reactivate" color="var(--success)" testid={`admin-reactivate-${u.id}`} />
                            : <IconBtn onClick={() => onDeactivate(u)} icon={Ban} label="Deactivate" testid={`admin-deactivate-${u.id}`} />}
                          <IconBtn onClick={() => onDelete(u)} icon={Trash2} label="Delete" color="var(--danger)" testid={`admin-delete-${u.id}`} />
                        </>
                      ) : (
                        <span className="text-[10px] text-[#90A4AE] italic pr-2">Not manageable</span>
                      )}
                    </div>
                  </div>
                  {isExp && matrix && (
                    <div className="pl-14 pr-4 pb-4 bg-[#F3FAFD]" data-testid={`admin-user-perms-${u.id}`}>
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A] mb-2 pt-2">
                        Effective access · {rm.label}
                      </div>
                      <PermissionsPreview role={u.role_canonical} matrix={matrix} />
                      {canManage(u) && (
                        <div className="md:hidden flex items-center gap-1 mt-3 pt-3 border-t border-black/5">
                          <IconBtn onClick={() => onEdit(u)} icon={Pencil} label="Edit" />
                          <IconBtn onClick={() => onReset(u)} icon={KeyRound} label="Reset" />
                          {u.is_deactivated
                            ? <IconBtn onClick={() => onReactivate(u)} icon={RotateCcw} label="Reactivate" color="var(--success)" />
                            : <IconBtn onClick={() => onDeactivate(u)} icon={Ban} label="Deactivate" />}
                          <IconBtn onClick={() => onDelete(u)} icon={Trash2} label="Delete" color="var(--danger)" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function IconBtn({ onClick, icon: Icon, label, color = "var(--text-muted)", testid }) {
  return (
    <button onClick={onClick} data-testid={testid} title={label}
            className="h-8 px-2.5 rounded-lg hover:bg-[#E7F5FB] text-[11px] flex items-center gap-1.5"
            style={{ color }}>
      <Icon size={11} /> <span className="hidden xl:inline">{label}</span>
    </button>
  );
}

function PermissionsPreview({ role, matrix }) {
  if (!matrix) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
      {matrix.modules.map(m => {
        const lvl = matrix.matrix[m]?.[role] || "none";
        const st = LEVEL_STYLE[lvl] || LEVEL_STYLE.none;
        return (
          <div key={m} className="flex items-center justify-between gap-2 py-2 px-2.5 rounded-xl bg-white border border-[#D7E8F0]">
            <span className="text-[11px] text-[#607D8B] capitalize truncate">{m.replace(/_/g, " ")}</span>
            <span className="text-[9px] uppercase tracking-wider px-2 py-1 rounded-full font-mono"
                  style={{ color: st.color, background: st.bg }}>{lvl}</span>
          </div>
        );
      })}
    </div>
  );
}

/* --------- Matrix view --------- */
function RBACMatrixView({ matrix, setMatrix, canEdit }) {
  const [savingKey, setSavingKey] = useState(null);
  if (!matrix) return null;
  const lockedModules = new Set(matrix.locked_modules || []);
  const levelOptions = Object.keys(matrix.level_definitions || {});

  async function changeLevel(module, r, level) {
    const key = `${module}::${r}`;
    const previous = matrix.matrix[module]?.[r] || "none";
    if (level === previous) return;
    setSavingKey(key);
    // Optimistic update — reverted on failure.
    setMatrix(m => ({ ...m, matrix: { ...m.matrix, [module]: { ...m.matrix[module], [r]: level } } }));
    try {
      await api.patch("/admin/rbac/matrix", { module, role: r, level });
      toast.success(`${ROLE_META[r]?.label || r} · ${module.replace(/_/g, " ")} → ${level}`);
    } catch (e) {
      setMatrix(m => ({ ...m, matrix: { ...m.matrix, [module]: { ...m.matrix[module], [r]: previous } } }));
      toast.error(e?.response?.data?.detail || "Could not update permission");
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="text-[11px] text-[#607D8B] bg-white border border-[#C9DCE6] rounded-2xl px-4 py-3">
          Click any cell to change what that role can do — it takes effect immediately, for everyone with that role.
          <span className="text-[#78909C]"> "Admin" and "Impersonation" are locked — those are enforced by dedicated code, not this table, so editing them here wouldn't change anything real. Super Admin stays "full" everywhere so there's always a way back in.</span>
        </div>
      )}
      <div className="border border-[#C9DCE6] rounded-[22px] bg-white overflow-hidden shadow-[0_10px_28px_rgba(7,83,154,0.08)]">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full text-[12px]" data-testid="rbac-matrix">
            <thead className="bg-[#E7F5FB] border-b border-[#C9DCE6]">
              <tr>
                <th className="text-left px-4 py-3.5 font-semibold text-[10px] uppercase tracking-widest text-[#07539A]">Module</th>
                {matrix.canonical_roles.map(r => (
                  <th key={r} className="text-left px-2 py-3.5 font-semibold text-[10px] uppercase tracking-widest whitespace-nowrap text-[#07539A]">
                    {ROLE_META[r]?.label || r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2EDF2]">
              {matrix.modules.map(m => {
                const rowLocked = lockedModules.has(m);
                return (
                  <tr key={m}>
                    <td className="px-4 py-3 text-[12px] font-medium text-[#243B53] capitalize">
                      {m.replace(/_/g, " ")}
                      {rowLocked && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-[#9CA3AF]" title={matrix.locked_reason}>· locked</span>}
                    </td>
                    {matrix.canonical_roles.map(r => {
                      const lvl = matrix.matrix[m]?.[r] || "none";
                      const st = LEVEL_STYLE[lvl] || LEVEL_STYLE.none;
                      const cellLocked = !canEdit || rowLocked || r === "super_admin";
                      const key = `${m}::${r}`;
                      if (cellLocked) {
                        return (
                          <td key={r} className="px-2 py-2">
                            <span className="text-[9px] uppercase tracking-wider px-2 py-1 rounded-full font-mono"
                                  style={{ color: st.color, background: st.bg }}>{lvl}</span>
                          </td>
                        );
                      }
                      return (
                        <td key={r} className="px-2 py-2">
                          <select
                            value={lvl}
                            disabled={savingKey === key}
                            onChange={e => changeLevel(m, r, e.target.value)}
                            data-testid={`rbac-cell-${m}-${r}`}
                            className="text-[9px] uppercase tracking-wider px-2 py-1 rounded-full font-mono border-0 cursor-pointer disabled:opacity-50"
                            style={{ color: st.color, background: st.bg }}
                          >
                            {levelOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-[#C9DCE6] rounded-[22px] bg-white p-5 shadow-[0_8px_22px_rgba(7,83,154,0.06)]">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#07539A] mb-3">Access levels</div>
          <div className="space-y-1.5">
            {Object.entries(matrix.level_definitions).map(([k, v]) => {
              const st = LEVEL_STYLE[k] || LEVEL_STYLE.none;
              return (
                <div key={k} className="flex items-center gap-2 text-[12px]">
                  <span className="text-[9px] uppercase tracking-wider px-2 py-1 rounded-full font-mono w-24 text-center shrink-0"
                        style={{ color: st.color, background: st.bg }}>{k}</span>
                  <span className="text-[#607D8B]">{v}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="border border-[#C9DCE6] rounded-[22px] bg-white p-5 shadow-[0_8px_22px_rgba(7,83,154,0.06)]">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#07539A] mb-3">Confidential fields</div>
          <div className="space-y-2 text-[12px]">
            {Object.entries(matrix.confidential_fields).map(([entity, fields]) => (
              <div key={entity}>
                <div className="text-[10px] uppercase tracking-wider text-[#78909C]">{entity}</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {fields.map(f => <span key={f} className="text-[10px] px-2 py-1 rounded-full bg-[#FFF1F0] text-[#B42318] font-mono">{f}</span>)}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-[#607D8B] italic border-t border-[#DCEAF0] pt-3">
            {matrix.founder_guardrail}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------- User form modal --------- */
function UserFormModal({ user, partners, matrix, onClose, onDone }) {
  const { role: actorRole } = useAuth();
  const isEdit = !!user;
  const currentRole = user?.role_canonical || user?.role;
  // Containment mirrors the backend: an Admin (not Super Admin) can't create or promote
  // into admin-tier accounts, and Super Admin is permanently capped at one account, so
  // neither "admin" nor "super_admin" is ever offered as a NEW target role from this modal
  // — except the user's own current role stays visible (unchanged) when editing them.
  // "partner" is excluded the same way: Partner accounts are created and promoted from
  // Partner Management only, never from here.
  const selectableRoles = (matrix?.canonical_roles || []).filter(r => {
    if (r === currentRole) return true;
    if (r === "super_admin") return false;
    if (r === "admin" && actorRole !== "super_admin") return false;
    if (r === "partner") return false;
    return true;
  });
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role_canonical || user?.role || "ergoniq_sales",
    partner_id: user?.partner_id || "",
    manager_id: user?.manager_id || "",
    phone: user?.phone || "",
    designation: user?.designation || "",
    department: user?.department || "",
    location: user?.location || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const needsPartner = form.role === "partner";

  async function submit() {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name & email are required");
    if (needsPartner && !form.partner_id) return toast.error("Select a partner firm for this role");
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(), email: form.email.trim(),
        role: form.role,
        partner_id: needsPartner ? form.partner_id : null,
        manager_id: form.manager_id || null,
        phone: form.phone || null,
        designation: form.designation || null,
        department: form.department || null,
        location: form.location || null,
      };
      if (!isEdit) body.password = form.password || undefined;
      let created = null;
      if (isEdit) await api.patch(`/admin/users/${user.id}`, body);
      else ({ data: created } = await api.post("/admin/users", body));
      // Auto-generated passwords come back once and are never retrievable again.
      if (created?.temp_password) {
        toast.success(`User created · temporary password: ${created.temp_password}`, { duration: 30000 });
      } else {
        toast.success(isEdit ? "User updated" : "User created");
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#062E55]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#EFF8FC] border border-[#BFD6E1] rounded-[24px] shadow-[0_28px_70px_rgba(7,42,73,0.28)] w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-testid="admin-user-form">
        <div className="px-5 py-4 bg-[#07539A] flex items-center gap-2 shrink-0 !text-white">
          <span className="w-8 h-8 rounded-xl bg-white/12 flex items-center justify-center"><UserPlus size={15} /></span>
          <div className="font-display text-[16px] flex-1 !text-white">{isEdit ? `Edit ${user.name}` : "Create User"}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center hover:bg-white/15"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="uf-name" />
            <Field label="Email *" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="uf-email" />
            <Field label="Phone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} testid="uf-phone" />
            <Field label="Designation" value={form.designation} onChange={v => setForm({ ...form, designation: v })} testid="uf-designation" />
            <Field label="Team / Department" value={form.department} onChange={v => setForm({ ...form, department: v })} testid="uf-department" />
            <Field label="Location" value={form.location} onChange={v => setForm({ ...form, location: v })} testid="uf-location" />
            {!isEdit && <Field label="Password (blank → auto-generated)" value={form.password} onChange={v => setForm({ ...form, password: v })} testid="uf-password" />}
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A] mb-2 block">Role</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {selectableRoles.map(r => {
                const rm = ROLE_META[r] || { label: r, color: "#6B7280", desc: "" };
                const active = form.role === r;
                return (
                  <button key={r} onClick={() => setForm({ ...form, role: r })} data-testid={`uf-role-${r}`}
                          className={`text-left p-3 rounded-2xl border transition-colors ${active ? "border-[#07539A] bg-[#E7F5FB] shadow-[0_4px_12px_rgba(7,83,154,0.08)]" : "border-[#C9DCE6] bg-white hover:border-[#75AFC8]"}`}>
                    <div className="text-[12px] font-medium" style={{ color: rm.color }}>{rm.label}</div>
                    <div className="text-[10px] text-[#607D8B] mt-0.5">{rm.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {needsPartner && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A] mb-1.5 block">Partner firm *</label>
              <select value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })} data-testid="uf-partner"
                      className="w-full h-10 px-3 rounded-xl bg-white border border-[#BFD6E1] focus:border-[#07539A] outline-none text-[13px]">
                <option value="">— Select partner firm —</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.firm} — {p.name}</option>)}
              </select>
            </div>
          )}

          {matrix && (
            <div className="border-t border-[#D1E4EC] pt-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A] mb-2 flex items-center gap-1">
                <Shield size={10} /> Effective access preview
              </div>
              <PermissionsPreview role={form.role} matrix={matrix} />
            </div>
          )}
        </div>
        <div className="p-4 border-t border-[#D1E4EC] shrink-0 bg-white/60">
          <button onClick={submit} disabled={saving} data-testid="uf-save"
                  className="w-full h-11 rounded-xl bg-[#E58A19] hover:bg-[#C96F08] !text-white text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_8px_20px_rgba(197,107,8,0.20)]">
            {saving ? <Loader2 size={12} className="animate-spin" /> : (isEdit ? <Check size={12} /> : <Plus size={12} />)}
            {isEdit ? "Save changes" : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A] mb-1.5 block">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
             className="w-full h-10 px-3 rounded-xl bg-white border border-[#BFD6E1] focus:border-[#07539A] outline-none text-[13px] shadow-[0_3px_8px_rgba(7,83,154,0.04)]" />
    </div>
  );
}

/* --------- Reset password modal --------- */
function ResetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit() {
    if (!pw || pw.length < 6) return toast.error("Min 6 characters");
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { password: pw });
      toast.success(`Password reset for ${user.name}. Share the new one securely.`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-[#062E55]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#EFF8FC] border border-[#BFD6E1] rounded-[24px] shadow-[0_28px_70px_rgba(7,42,73,0.28)] w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-testid="admin-reset-modal">
        <div className="px-5 py-4 bg-[#07539A] flex items-center gap-2 !text-white">
          <span className="w-8 h-8 rounded-xl bg-white/12 flex items-center justify-center"><KeyRound size={15} /></span>
          <div className="font-display text-[16px] flex-1 !text-white">Reset password · {user.name}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center hover:bg-white/15"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[12px] text-[#607D8B]">
            Set a new password for <span className="font-mono">{user.email}</span>. The user will use this at their next login.
          </p>
          <Field label="New password (min 6)" value={pw} onChange={setPw} testid="rp-password" />
          <button onClick={submit} disabled={saving} data-testid="rp-save"
                  className="w-full h-11 rounded-xl bg-[#E58A19] hover:bg-[#C96F08] !text-white text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_8px_20px_rgba(197,107,8,0.20)]">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
            Reset password
          </button>
        </div>
      </div>
    </div>
  );
}
